import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { UserPlus, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { LEAD_STAGES, getBackendValueByStageId } from './constants';

export const NewLeadModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  
  // Estado inicial del formulario
  const initialFormData = {
    name: '',
    estimated_annual_premium: '',
    policy_type: 'Autos', 
    stage: 'new', // ID interno (default: 'new' = Prospecto Nuevo)
    notes: ''
  };
  
  const [formData, setFormData] = useState(initialFormData);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    // Validación básica
    if (!formData.name.trim()) {
      return toast.error("El nombre del prospecto es obligatorio");
    }

    setLoading(true);
    try {
      // Preparar payload para API
      const payload = {
        name: formData.name,
        estimated_annual_premium: formData.estimated_annual_premium ? parseFloat(formData.estimated_annual_premium) : 0,
        notes: `Interés: ${formData.policy_type}. ${formData.notes}`,
        stage: getBackendValueByStageId(formData.stage) // Enviar valor string backend
      };

      const response = await api.post('/leads', payload);
      
      // Enriquecer la respuesta para el frontend (adaptador visual)
      const newLeadVisual = {
        id: response.data.id,
        name: response.data.name,
        company: '', // Backend no tiene company por defecto
        product: formData.policy_type, // Guardamos localmente para visualización inmediata
        value: payload.estimated_annual_premium,
        stage: 'new', // Mapeo simple, o usar función de mapeo si está disponible
        updatedAt: new Date().toISOString()
      };

      toast.success("✅ Prospecto creado exitosamente");
      onSuccess(response.data); // Pasamos data cruda o visual según necesite el padre
      
      // Reset y cerrar
      setFormData(initialFormData);
      onClose();
      
    } catch (error) {
      console.error("Error creando lead:", error);
      toast.error(error.response?.data?.detail || "Error al crear prospecto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="bg-indigo-100 p-2 rounded-full">
              <UserPlus className="h-5 w-5 text-indigo-600" />
            </div>
            <DialogTitle>Nuevo Prospecto</DialogTitle>
          </div>
          <DialogDescription>
            Registra una nueva oportunidad de venta rápida.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Nombre */}
          <div className="grid gap-2">
            <Label htmlFor="name">Nombre del Prospecto *</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ej. Juan Pérez"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Monto */}
            <div className="grid gap-2">
              <Label htmlFor="estimated_annual_premium">Valor Estimado ($)</Label>
              <Input
                id="estimated_annual_premium"
                name="estimated_annual_premium"
                type="number"
                value={formData.estimated_annual_premium}
                onChange={handleChange}
                placeholder="0.00"
              />
            </div>

            {/* Producto */}
            <div className="grid gap-2">
              <Label htmlFor="policy_type">Producto Interés</Label>
              <Select 
                value={formData.policy_type} 
                onValueChange={(val) => handleSelectChange('policy_type', val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Autos">Autos</SelectItem>
                  <SelectItem value="GMM">Gastos Médicos</SelectItem>
                  <SelectItem value="Vida">Vida</SelectItem>
                  <SelectItem value="Hogar">Hogar</SelectItem>
                  <SelectItem value="Daños">Daños</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Etapa */}
          <div className="grid gap-2">
            <Label htmlFor="stage">Etapa Inicial</Label>
            <Select 
              value={formData.stage} 
              onValueChange={(val) => handleSelectChange('stage', val)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona etapa" />
              </SelectTrigger>
              <SelectContent>
                {LEAD_STAGES.map(stage => (
                  <SelectItem key={stage.id} value={stage.id}>
                    {stage.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Notas */}
          <div className="grid gap-2">
            <Label htmlFor="notes">Notas Rápidas</Label>
            <Input
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Detalles adicionales..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            <X className="mr-2 h-4 w-4" />
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !formData.name} className="bg-indigo-600 hover:bg-indigo-700">
            <Save className="mr-2 h-4 w-4" />
            {loading ? 'Guardando...' : 'Guardar Prospecto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
